import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * About Us — Halil's page 5.
 *
 * The document's own instruction is followed here: the four-quadrant
 * human/agent explanation is on the home page and this page links to it rather
 * than repeating it. Restating a differentiator two clicks apart weakens it.
 */
export const about: WrittenPage = {
  navLabel: 'About Us',
  title: 'About Digitopia',
  description:
    'Why Digitopia exists, what we believe, and who runs it. Founded 2019. New York, London, Istanbul. 300+ customers across 40+ countries.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'About Digitopia',
      headline: 'We are Digitopians.',
      sub: 'A firm built for the moment software stopped advising and started acting.',
      photo: {
        src: '/photo/open-office-team.jpg',
        alt: 'Digitopia colleagues working together in an open-plan office, a plan sketched on the whiteboard behind them.',
      },
    },
    {
      kind: 'prose',
      heading: 'Why we exist',
      paragraphs: [
        'Digitopia exists to maximize business outcomes by deploying agentic AI solutions for visionary businesses, with teams of forward deployed engineers and AI agents.',
        'Consulting told companies what to do and left. Software gave them tools and left. Neither reliably produced an outcome. We built the firm we thought was missing: human judgment where judgment matters, agentic execution everywhere it does not, and a standard of trust an enterprise can sign.',
      ],
    },
    {
      kind: 'features',
      columns: 3,
      items: [
        { title: 'Human Guidance', body: '[Strategy consultants](/en/solutions/strategy-consulting), [transformation managers](/en/solutions/transformation-management), forward deployed engineers, AI engineers.' },
        { title: 'Business Solutions', body: '[Agentic AI frameworks](/en/solutions/enterprise-ai-solutions), business ontologies, business agents, technology agents.' },
        { title: 'Trust', body: '[Privacy](/en/privacy-policy), compliance, governance, sovereignty.' },
      ],
      footnote: 'Most firms sell you one of these three. Outcome requires all three.',
    },
    {
      kind: 'features',
      heading: 'Purpose, mission, vision',
      columns: 3,
      textSize: 'lg',
      items: [
        { title: 'Purpose', note: '#ScaleValue', body: 'Value that scales, for the businesses we work with and for the people in them.' },
        { title: 'Mission', note: '#TransformBetter', body: 'Transformation done better: measured, sequenced, and actually finished.' },
        { title: 'Vision', body: 'The future is a digital utopia, which we call Digitopia.' },
      ],
    },
    {
      kind: 'stats',
      heading: 'At a glance',
      items: [
        { figure: '300+', label: 'customers' },
        { figure: '50+', label: 'partners' },
        { figure: '600+', label: 'practitioners' },
        { figure: '40+', label: 'countries' },
        { figure: '20+', label: 'industries' },
        { figure: '3,000+', label: 'measurements' },
      ],
      footnote: 'Founded 2019. New York. London. Istanbul.',
      check:
        'Confirm the definitions behind 50+ partners, 600+ practitioners and 3,000+ ' +
        'measurements before these go public. "Practitioners" in particular reads as ' +
        'headcount unless the page says it includes certified partner practitioners. And ' +
        'these figures must match the ones used on the home page — see the check there.',
    },
    {
      kind: 'features',
      heading: 'Three CEOs. Three focuses.',
      columns: 3,
      items: [
        {
          title: 'Halil Aksu',
          note: 'Focus: Growth',
          // Back to LinkedIn as of 2026-09-15: the contact page at
          // /en/about/team/halil-aksu is reached by scanning its QR code and by
          // nothing else, so no link on the site points at it.
          href: 'https://www.linkedin.com/in/halilaksu/',
          photo: {
            src: '/photo/halil-aksu-portrait.webp',
            alt: 'Halil Aksu, Co-CEO of Digitopia',
          },
        },
        {
          title: 'Kagan Sahin',
          note: 'Focus: Operations',
          // Back to LinkedIn as of 2026-09-15, same reason as the card above:
          // /en/about/team/kagan-sahin is a QR-code destination, not somewhere
          // the site navigates to.
          href: 'https://www.linkedin.com/in/kagansahin/',
          photo: {
            src: '/photo/kagan-sahin-portrait.webp',
            alt: 'Kagan Sahin, Co-CEO of Digitopia',
          },
        },
        {
          title: 'Ahmet Bilgen',
          note: 'Focus: Technology',
          // Back to LinkedIn as of 2026-09-15, same reason as the two cards
          // above: /en/about/team/ahmet-bilgen is a QR-code destination, not
          // somewhere the site navigates to.
          href: 'https://www.linkedin.com/in/abilgen/',
          photo: {
            src: '/photo/ahmet-bilgen-portrait.jpg',
            alt: 'Ahmet Bilgen, Co-CEO of Digitopia',
          },
        },
      ],
      check:
        'The Growth/Operations/Technology order was assigned to Halil/Kagan/Ahmet by ' +
        'matching the order they were already listed in against the order the removed ' +
        'sub-heading named the three focuses — nobody confirmed which co-CEO actually owns ' +
        'which. Portraits and LinkedIn links were supplied 2026-08-31 and are in place; the ' +
        'name now links to the profile and opens in a new tab. Also confirm how Ahmet should ' +
        'be described: he joined as a later co-founder, and the current digitopia.co copy ' +
        'credits the 2019 founding to Halil and Kagan.',
    },
    // The values are this band and nothing else now — the reference supplied
    // 2026-08-31 is the picture on the left, the line beside it and one button,
    // *"komple olarak sadece böyle olsun"*. The eight values themselves moved to
    // their own page, `content/pages/our-values.ts`, which both the picture and
    // the button open; the grid that used to sit under this section is gone from
    // here rather than duplicated there.
    {
      kind: 'split',
      flip: true,
      heading: 'We bring light to darkness.',
      paragraphs: ['Read more about our values…'],
      cta: { label: 'Our Values', href: '/en/about/our-values' },
      photo: {
        src: '/photo/axolotl-our-values.webp',
        alt: 'The Digitopia axolotl, pale and wide-eyed, on a dark rounded panel edged in a violet-to-pink gradient.',
      },
      photoHref: '/en/about/our-values',
      photoFit: 'contain',
    },
    {
      kind: 'prose',
      heading: 'Judgment where it matters. Execution everywhere else.',
      paragraphs: [
        'Consulting firms staff problems with people. Software firms hand you a platform. Both are honest about what they sell, and neither on its own gets you to an outcome, because judgment and scale are not the same capability.',
        'We run both. Consultants set the frame and make the calls that need a human. Agents carry the load underneath, to the same standard, every time. Patterns from 300+ companies flow back into the frameworks, so what you buy from us improves after you have bought it.',
      ],
    },
    {
      kind: 'cta',
      heading: 'Become a Digitopian.',
      sub: 'We hire for courage, ownership and curiosity. The rest is learnable, and we will help you learn it.',
      tone: 'band',
      headingHref: LINKS.careers,
      ctas: [{ label: 'See open roles', href: LINKS.careers }],
    },
    // The dark "Let us talk about your outcome." band that used to close this
    // page is gone (2026-08-29 — "bu siyah yere gerek yok zaten bizim kendi
    // contact us tasarımımız var"). Every document already ends with the
    // gradient `ContactBand` above the footer, so a second contact ask stacked
    // directly on top of it asked the same thing twice, in two designs.
  ],
}
